/**
 * Choose the identity document: issuing country first, then the documents that
 * country can present (./documents `documentsFor`). Shown only when the step
 * offers more than one document; choosing hands back the document and the
 * country, and the camera opens on that document.
 */
import { useTranslation } from 'react-i18next';

import { CountrySelect } from './CountrySelect';
import { documentsFor, type DocumentKind } from './documents';

interface DocumentChoiceProps {
  /** The documents this step accepts. */
  offered: DocumentKind[];
  /** Held by the caller so it survives going back from the guide. */
  country: string;
  onCountryChange: (country: string) => void;
  onChoose: (kind: DocumentKind) => void;
}

export function DocumentChoice({ offered, country, onCountryChange, onChoose }: DocumentChoiceProps) {
  const { t } = useTranslation();
  const kinds = documentsFor(country).filter((kind) => offered.includes(kind));

  return (
    <section className="doc-choice" data-testid="document-choice">
      <h1 className="step-title">{t('flow.documentChoice.title')}</h1>
      <div className="details-field">
        <span className="details-label" aria-hidden="true">
          {t('flow.documentChoice.country')}
        </span>
        <CountrySelect
          name="issuingCountry"
          value={country}
          onChange={onCountryChange}
          label={t('flow.documentChoice.country')}
          placeholder={t('flow.documentChoice.country')}
        />
      </div>
      {kinds.length === 0 ? (
        <p className="step-text" role="status">
          {t('flow.documentChoice.none')}
        </p>
      ) : null}
      <ul className="doc-choice__list">
        {kinds.map((kind) => (
          <li key={kind}>
            <button
              type="button"
              className="doc-choice__option"
              data-track="document_option"
              data-testid={`document-${kind}`}
              onClick={() => onChoose(kind)}
            >
              <span>{t(`flow.documentChoice.kind.${kind}`)}</span>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
